import type {
	OnChangeFn,
	RowSelectionState,
	Updater,
} from "@tanstack/react-table";
import { useCallback, useEffect, useState } from "react";

export const EMPTY_ROW_SELECTION: RowSelectionState = {};

function applyUpdater<T>(updater: Updater<T>, previous: T): T {
	return typeof updater === "function"
		? (updater as (old: T) => T)(previous)
		: updater;
}

interface ScopedRowSelectionOptions {
	/**
	 * When true, a selection can reappear if the user returns to a previous scope
	 * before selecting anything in another scope. Defaults to the mobile behavior.
	 */
	restoreOnScopeReturn?: boolean;
}

export function useScopedRowSelectionState(
	selectionScopeKey: string,
	{ restoreOnScopeReturn = true }: ScopedRowSelectionOptions = {},
) {
	const [selectionState, setSelectionState] = useState(() => ({
		active: false,
		rows: EMPTY_ROW_SELECTION,
		scopeKey: "",
	}));

	useEffect(() => {
		if (restoreOnScopeReturn) return;
		setSelectionState((previous) => {
			if (
				previous.scopeKey === selectionScopeKey &&
				!previous.active &&
				previous.rows === EMPTY_ROW_SELECTION
			) {
				return previous;
			}
			return {
				active: false,
				rows: EMPTY_ROW_SELECTION,
				scopeKey: selectionScopeKey,
			};
		});
	}, [restoreOnScopeReturn, selectionScopeKey]);

	const selectionIsCurrent = selectionState.scopeKey === selectionScopeKey;
	const rowSelection = selectionIsCurrent
		? selectionState.rows
		: EMPTY_ROW_SELECTION;
	const selectionMode = selectionState.active && selectionIsCurrent;

	const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
		(updater) => {
			setSelectionState((previous) => {
				const previousRows =
					previous.scopeKey === selectionScopeKey
						? previous.rows
						: EMPTY_ROW_SELECTION;
				return {
					active: true,
					rows: applyUpdater(updater, previousRows),
					scopeKey: selectionScopeKey,
				};
			});
		},
		[selectionScopeKey],
	);

	const setSelectedRowIds = useCallback(
		(rows: RowSelectionState, active = true) => {
			setSelectionState({ active, rows, scopeKey: selectionScopeKey });
		},
		[selectionScopeKey],
	);

	const clearSelection = useCallback(() => {
		setSelectionState({
			active: false,
			rows: EMPTY_ROW_SELECTION,
			scopeKey: selectionScopeKey,
		});
	}, [selectionScopeKey]);

	return {
		rowSelection,
		selectionMode,
		handleRowSelectionChange,
		setSelectedRowIds,
		clearSelection,
	};
}
